import { useState, useEffect, useRef } from "react";
import axios from "axios";
import css from "../index.module.css";
import Popup from "@components/popup";

import { toast } from "sonner";
import useUserData from "@hooks/useUserData";

export default function Organizations({ page, setPage }: { page: number, setPage: (page: number) => void }) {
  const { refreshUserData } = useUserData();
  const [inn, setInn] = useState("");
  const [loadingData, setLoadingData] = useState(false);
  const [orgInfo, setOrgInfo] = useState<any>(null);
  const lastRequestedInn = useRef<string>("");
  const innDebounceTimer = useRef<NodeJS.Timeout | null>(null);
  const [organizations, setOrganizations] = useState<any>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchOrganizations = () => {
    return axios
      .get(`${import.meta.env.VITE_API_URL}/organizations`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        setOrganizations(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (!confirmDeleteOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setConfirmDeleteOpen(false);
        setOrgToDelete(null);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [confirmDeleteOpen]);

  const openDeleteConfirm = (item: any) => {
    setOrgToDelete(item);
    setConfirmDeleteOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (deleteLoading) return;
    setConfirmDeleteOpen(false);
    setOrgToDelete(null);
  };

  const setConfirmDeleteOpenSafe = (value: boolean) => {
    if (value) {
      setConfirmDeleteOpen(true);
      return;
    }
    closeDeleteConfirm();
  };

  const confirmDelete = async () => {
    if (!orgToDelete?.id) return;

    setDeleteLoading(true);
    try {
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/organizations/${orgToDelete.id}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      toast.success("Организация удалена");
      await fetchOrganizations();
      closeDeleteConfirm();
    } catch (error: any) {
      console.log(error);
      toast.error("Не удалось удалить организацию");
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    if (innDebounceTimer.current) {
      clearTimeout(innDebounceTimer.current);
      innDebounceTimer.current = null;
    }

    const isInnComplete =
      (inn.length === 10 || inn.length === 12) && /^\d+$/.test(inn);

    if (!isInnComplete) {
      if (inn.length < 10) {
        setOrgInfo(null);
        lastRequestedInn.current = "";
        setLoadingData(false);
      }
      return;
    }

    const isNewInn = inn !== lastRequestedInn.current;

    if (isNewInn) {
      innDebounceTimer.current = setTimeout(() => {
        const currentInn = inn;
        const stillComplete =
          (currentInn.length === 10 || currentInn.length === 12) &&
          /^\d+$/.test(currentInn);
        const stillNew = currentInn !== lastRequestedInn.current;

        if (stillComplete && stillNew) {
          lastRequestedInn.current = currentInn;
          setLoadingData(true);
          setOrgInfo(null);

          axios
            .get(
              `${
                import.meta.env.VITE_API_URL
              }/dadata/findOrg?query=${currentInn}`,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
              }
            )
            .then((response) => {
              if (response.data.status == "Error") {
                setLoadingData(false);
                lastRequestedInn.current = "";
                toast.error("Ошибка при поиске организации");
                return;
              }

              console.log(response.data);

              setOrgInfo(response.data);
              setLoadingData(false);
            })
            .catch((error) => {
              setLoadingData(false);
              lastRequestedInn.current = "";
              console.log(error);
            });
        }
      }, 800);
    }

    return () => {
      if (innDebounceTimer.current) {
        clearTimeout(innDebounceTimer.current);
      }
    };
  }, [inn, page]);

  const handleSubmitOrganization = (event: any) => {
    event.preventDefault();

    axios
      .post(`${import.meta.env.VITE_API_URL}/organizations`, {
        type: orgInfo.data.type,
        name: orgInfo.value,
        inn: orgInfo.data.inn,
        ogrn: orgInfo.data.ogrn,
        status: orgInfo.data.state.status,
        address: orgInfo.data.address.unrestricted_value,
      }, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((_) => {
        toast.success("Данные успешно обновлены");
        refreshUserData();
      })
      .catch((_) => {
        toast.error("Ошибка обновления данных");
      });
  };

  return (
    <>
        <Popup showPopup={confirmDeleteOpen} setShowPopup={setConfirmDeleteOpenSafe}>
          <div className={css.deleteConfirmPopup}>
            <h1>Удалить организацию?</h1>
            <a className={css.info}>
              {orgToDelete?.name ? `${orgToDelete.name} — ` : ""}
              {orgToDelete?.address || ""}
            </a>
            <div className={css.confirmActions}>
              <button
                type="button"
                className={css.cancelBtn}
                onClick={closeDeleteConfirm}
                disabled={deleteLoading}
              >
                Отмена
              </button>
              <button
                type="button"
                className={css.dangerBtn}
                onClick={confirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Удаляем..." : "Удалить"}
              </button>
            </div>
          </div>
        </Popup>
        {page === 5 && (
          <div className={css.col1}>
            <div className={css.accountInfo}>
              <button
                type="button"
                style={{ width: "100%", marginBottom: 10 }}
                onClick={() => setPage(6)}
              >
                Добавить организацию
              </button>

              {organizations.map((item: any) => (
                <div key={item.id} className={css.addressItem}>
                  <h3 className={css.orgTitle}>{item.status === 'ACTIVE' ? <div className={css.circleActive} /> : <div className={css.circleInactive} />} {item.name}</h3>
                  <p>{item.address}</p>
                  <button
                    type="button"
                    className={css.deleteAddressBtn}
                    onClick={() => openDeleteConfirm(item)}
                  >
                    <i className="fi fi-rr-trash"></i> Удалить организацию
                  </button>
                </div>
              ))}

              {organizations.length <= 0 && (
                <a style={{ fontSize: 18 }}>организаций нет...</a>
              )}
            </div>
          </div>
        )}

        {page === 6 && (
          <div className={css.col1}>
          <div className={css.accountInfo}>
            <form className={css.form} onSubmit={handleSubmitOrganization}>
              <div className={css.flex}>
                <b className={css.headAcc}>Добавить организацию</b>

                <input
                  placeholder="ИНН вашей организации"
                  type="text"
                  name="inn"
                  value={inn}
                  onChange={(e: any) => {
                    const val = e.target.value.replace(/\D/g, "").slice(0, 12);
                    setInn(val);
                  }}
                  pattern="\d{10,12}"
                  minLength={10}
                  maxLength={12}
                  required
                  inputMode="numeric"
                />

                {loadingData && (
                  <div className={css.loadingInfo}>
                    <b>Ищем вашу организацию...</b>
                    <p>Подождите, пожалуйста...</p>
                  </div>
                )}
                
                {!loadingData && orgInfo && (
                  <div className={css.orgInfo}>
                    <div className={css.infoItem}>
                      <b>{orgInfo.value}</b>
                      <p>{orgInfo.data.address.unrestricted_value}</p>
                    </div>

                    <div
                      className={`${css.statusInfo} ${
                        orgInfo.data.state.status === "ACTIVE"
                          ? css.active
                          : css.inactive
                      }`}
                    >
                      <span>
                        {orgInfo.data.state.status === "ACTIVE"
                          ? "Действующая"
                          : "Ликвидирована"}
                      </span>
                    </div>
                  </div>
                )}

              </div>
              <button>Сохранить организацию</button>
            </form>
          </div>
        </div>
        )}
    </>
  );
}
